/**
 * API client functions for medication catalog operations
 */

import { API_ENDPOINTS } from '../config/api';
import { MedicationCatalogItem, MedicationCatalogCreate, MedicationCatalogUpdate } from '../types/medication';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

/**
 * Get all medication catalog items for a practice
 */
export async function getMedicationCatalog(
  practiceId: string,
  search?: string,
  activeOnly: boolean = true
): Promise<MedicationCatalogItem[]> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('active_only', activeOnly.toString());

  const url = `${API_ENDPOINTS.MEDICATION_CATALOG.LIST(practiceId)}?${params.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch medication catalog items' }));
    throw new Error(error.detail || 'Failed to fetch medication catalog items');
  }

  return response.json();
}

/**
 * Create a new medication catalog item
 */
export async function createMedicationCatalogItem(
  practiceId: string,
  data: MedicationCatalogCreate
): Promise<MedicationCatalogItem> {
  const response = await fetch(API_ENDPOINTS.MEDICATION_CATALOG.CREATE(practiceId), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create medication catalog item' }));
    throw new Error(error.detail || 'Failed to create medication catalog item');
  }

  return response.json();
}

/**
 * Update a medication catalog item
 */
export async function updateMedicationCatalogItem(
  practiceId: string,
  itemId: string,
  data: MedicationCatalogUpdate
): Promise<MedicationCatalogItem> {
  const response = await fetch(API_ENDPOINTS.MEDICATION_CATALOG.UPDATE(practiceId, itemId), {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update medication catalog item' }));
    throw new Error(error.detail || 'Failed to update medication catalog item');
  }

  return response.json();
}

export async function getMedicationCatalogItem(
  practiceId: string,
  itemId: string
): Promise<MedicationCatalogItem> {
  const response = await fetch(`${API_ENDPOINTS.MEDICATION_CATALOG.LIST(practiceId)}/${itemId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch medication catalog item' }));
    throw new Error(error.detail || 'Failed to fetch medication catalog item');
  }
  return response.json();
}

/**
 * Delete (soft delete) a medication catalog item
 */
export async function deleteMedicationCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(API_ENDPOINTS.MEDICATION_CATALOG.DELETE(practiceId, itemId), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete medication catalog item' }));
    throw new Error(error.detail || 'Failed to delete medication catalog item');
  }
}

/**
 * Hard delete a medication catalog item (permanently removes it and linked billing item)
 */
export async function hardDeleteMedicationCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(
    `${API_ENDPOINTS.MEDICATION_CATALOG.DELETE(practiceId, itemId)}/hard`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete medication catalog item' }));
    throw new Error(error.detail || 'Failed to delete medication catalog item');
  }
}

/**
 * Sync medication catalog items to billing catalog
 */
export async function syncMedicationCatalogToBilling(practiceId: string): Promise<{ created: number; skipped: number }> {
  const response = await fetch(API_ENDPOINTS.MEDICATION_CATALOG.SYNC, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ practice_id: practiceId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to sync medication catalog' }));
    throw new Error(error.detail || 'Failed to sync medication catalog');
  }

  return response.json();
}

/**
 * Import medication items from billing catalog into medication catalog
 * This creates MedicationCatalog entries for billing items that have category MEDICATION
 * but don't have a linked_medication_id
 */
export async function importMedicationsFromBilling(practiceId: string): Promise<{ catalog_items_created: number; billing_items_linked: number }> {
  const response = await fetch(API_ENDPOINTS.MEDICATION_CATALOG.IMPORT_FROM_BILLING, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ practice_id: practiceId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to import medications from billing' }));
    throw new Error(error.detail || 'Failed to import medications from billing');
  }

  return response.json();
}

